import {
  ChatApiError,
  type ChatConversation,
  type ChatConversationSummary,
  type ChatDirectoryEntry,
  type ChatMessage,
} from "./chat-api";

/**
 * TUT-296b — o espelho do chat em modo demonstração.
 *
 * Segue o precedente de `portal-demo.ts`: em vez de a tela falar com
 * `/api/chat/...` (que não existe em `VITE_API_MODE=demo`, servido por
 * `vite preview` sem API por trás — é o que a e2e usa), o cliente de
 * `chat-api.ts` desvia para este módulo. Estado em memória do MÓDULO, não em
 * React: sobrevive a re-render, morre no reload — o bastante para a e2e e
 * para a demonstração.
 *
 * TUT-545 — o espelho da regra do dono (27/10): quem é de fora fala SÓ com o
 * escritório, pela conversa fixa (`ESCRITORIO`). A conversa da advogada do
 * harness do portal é a MESMA trilha nos dois lados: o que ela escreve chega à
 * Caixa Única como não lida, e a resposta da equipe chega ao portal. Direta,
 * grupo e agenda dão 403 no portal; a conversa antiga com alguém de fora fica
 * só leitura (409 ao postar); a equipe não cria direta nem grupo com alguém de
 * fora (409). Os códigos e as frases são os da API (`chat.service.ts`).
 */

interface Trilha {
  conversation: ChatConversation;
  messages: ChatMessage[];
}

interface Loja {
  meId: string;
  meName: string;
  portal: boolean;
  threads: Trilha[];
  /** As não lidas são de quem lê: a mesma trilha conta diferente em cada lado. */
  unread: Record<string, number>;
  directory: ChatDirectoryEntry[];
}

const EXTERNO = "EXTERNAL";

const PORTAL_SO_ESCRITORIO = () =>
  new ChatApiError("No portal, a conversa é com o escritório.", 403, "CHAT_PORTAL_ONLY_OFFICE");
const EXTERNO_SO_ESCRITORIO = () =>
  new ChatApiError(
    "Quem é de fora fala com o escritório pela conversa do portal, e não por conversa direta ou grupo.",
    409,
    "CHAT_EXTERNAL_ONLY_OFFICE",
  );
const CONVERSA_ENCERRADA = () =>
  new ChatApiError(
    "Esta conversa foi encerrada: quem é de fora agora fala com o escritório pela conversa do portal.",
    409,
    "CHAT_PORTAL_CONVERSA_ENCERRADA",
  );

function haMinutos(min: number): string {
  return new Date(Date.now() - min * 60_000).toISOString();
}

/** A regra de `conversaEncerrada` da API (`apps/api/src/domain/chat.ts`). */
function encerrada(conversa: Pick<ChatConversation, "kind" | "participants">): boolean {
  return conversa.kind !== "ESCRITORIO" && conversa.participants.some((p) => p.role === EXTERNO);
}

const EQUIPE = { id: "usr-renata", name: "Renata Silva" };
const ADVOGADA = { id: "portal-demo-user", name: "Dra. Marina Externa" };

/**
 * A conversa da advogada do harness com o escritório, já criada (ela escreveu
 * antes e a equipe respondeu). Do lado do escritório traz o externo e quem já
 * leu; o portal recorta os participantes para ela mesma (`vista`).
 */
const conversaDoEscritorio: Trilha = {
  conversation: {
    id: "chat-demo-escritorio-1",
    kind: "ESCRITORIO",
    title: null,
    externalUserId: ADVOGADA.id,
    readOnly: false,
    participants: [
      { userId: ADVOGADA.id, name: ADVOGADA.name, role: EXTERNO },
      { userId: EQUIPE.id, name: EQUIPE.name, role: "Administradora" },
    ],
    createdAt: haMinutos(300),
    updatedAt: haMinutos(15),
    lastMessageAt: haMinutos(15),
  },
  messages: [
    {
      id: "chat-demo-escritorio-msg-1",
      conversationId: "chat-demo-escritorio-1",
      authorUserId: ADVOGADA.id,
      authorName: ADVOGADA.name,
      body: "Enviei os documentos da perícia pelo portal. Chegaram?",
      createdAt: haMinutos(40),
    },
    {
      id: "chat-demo-escritorio-msg-2",
      conversationId: "chat-demo-escritorio-1",
      authorUserId: EQUIPE.id,
      authorName: EQUIPE.name,
      body: "Recebemos os documentos da perícia, obrigada por enviar!",
      createdAt: haMinutos(15),
    },
  ],
};

/**
 * TUT-561 (decisão do dono em 29/10) — quem conversa no chat da demonstração
 * é a equipe ENCENADA (`membrosIniciaisDaDemo`, em `lib/demo/membros.ts`), e
 * não gente inventada só aqui: é a lista das fotos da equipe que dá o avatar,
 * e uma pessoa fora da equipe nunca teria foto. A Equipe de Operações nasce
 * com foto na ficha (`lib/demo/ficha-da-equipe.ts`); a assistente, não. Os
 * ids e os nomes vão escritos por extenso porque este arquivo entra no build
 * de produção, que não pode carregar `lib/demo/*` (`bundle:budget`); quem
 * prende os dois juntos é `chat-demo.test.ts`.
 */
export const PESSOAS_DO_CHAT_DA_DEMO = {
  operacao: { id: "usr-operacao", name: "Equipe de Operações" },
  assistente: { id: "usr-assistente", name: "Assistente sem revisão" },
} as const;

function criarSessaoInterna(): Loja {
  const meId = EQUIPE.id;
  const meName = EQUIPE.name;
  const outroId: string = PESSOAS_DO_CHAT_DA_DEMO.operacao.id;
  const outroName: string = PESSOAS_DO_CHAT_DA_DEMO.operacao.name;
  const peritaId: string = PESSOAS_DO_CHAT_DA_DEMO.assistente.id;
  const peritaName: string = PESSOAS_DO_CHAT_DA_DEMO.assistente.name;

  const direct: Trilha = {
    conversation: {
      id: "chat-demo-direct-1",
      kind: "DIRECT",
      title: null,
      externalUserId: null,
      readOnly: false,
      participants: [
        { userId: meId, name: meName, role: "Administradora" },
        { userId: outroId, name: outroName, role: "Membro" },
      ],
      createdAt: haMinutos(120),
      updatedAt: haMinutos(6),
      lastMessageAt: haMinutos(6),
    },
    messages: [
      {
        id: "chat-demo-msg-1",
        conversationId: "chat-demo-direct-1",
        authorUserId: outroId,
        authorName: outroName,
        body: "Bom dia! O laudo da perícia 0007654 já foi protocolado?",
        createdAt: haMinutos(20),
      },
      {
        id: "chat-demo-msg-2",
        conversationId: "chat-demo-direct-1",
        authorUserId: outroId,
        authorName: outroName,
        body: "Preciso confirmar antes da audiência de amanhã.",
        createdAt: haMinutos(6),
      },
    ],
  };

  const group: Trilha = {
    conversation: {
      id: "chat-demo-group-1",
      kind: "GROUP",
      title: "Equipe trabalhista",
      externalUserId: null,
      readOnly: false,
      participants: [
        { userId: meId, name: meName, role: "Administradora" },
        { userId: outroId, name: outroName, role: "Membro" },
        { userId: peritaId, name: peritaName, role: "Membro" },
      ],
      createdAt: haMinutos(4_000),
      updatedAt: haMinutos(40),
      lastMessageAt: haMinutos(40),
    },
    messages: [
      {
        id: "chat-demo-msg-3",
        conversationId: "chat-demo-group-1",
        authorUserId: peritaId,
        authorName: peritaName,
        body: "Subi os quesitos respondidos da perícia 0002468.",
        createdAt: haMinutos(90),
      },
      // TUT-491 — a menção que põe o grupo em "Minhas" (C3).
      {
        id: "chat-demo-msg-4",
        conversationId: "chat-demo-group-1",
        authorUserId: outroId,
        authorName: outroName,
        body: `@${meName}, a diligência de quinta precisa do decibelímetro.`,
        mentionedUserIds: [meId],
        createdAt: haMinutos(40),
      },
    ],
  };

  // TUT-491 — uma conversa direta de antes com quem usa o portal. TUT-545:
  // ficou só leitura na Caixa (ele agora fala pela conversa com o escritório).
  const portal: Trilha = {
    conversation: {
      id: "chat-demo-portal-1",
      kind: "DIRECT",
      title: null,
      externalUserId: null,
      readOnly: true,
      participants: [
        { userId: meId, name: meName, role: "Administradora" },
        { userId: "usr-marcos-externo", name: "Dr. Marcos Tavares", role: EXTERNO },
      ],
      createdAt: haMinutos(3_000),
      updatedAt: haMinutos(70),
      lastMessageAt: haMinutos(70),
    },
    messages: [
      {
        id: "chat-demo-msg-5",
        conversationId: "chat-demo-portal-1",
        authorUserId: "usr-marcos-externo",
        authorName: "Dr. Marcos Tavares",
        body: "A reclamada pediu esclarecimentos. Há previsão de resposta?",
        createdAt: haMinutos(70),
      },
    ],
  };

  // A agenda é só a equipe (TUT-545: sem quem é de fora).
  const directory: ChatDirectoryEntry[] = [
    { userId: outroId, name: outroName, role: "Membro" },
    { userId: peritaId, name: peritaName, role: "Membro" },
  ];

  return {
    meId,
    meName,
    portal: false,
    threads: [direct, group, portal, conversaDoEscritorio],
    unread: { [direct.conversation.id]: 2, [group.conversation.id]: 1, [portal.conversation.id]: 1, [conversaDoEscritorio.conversation.id]: 0 },
    directory,
  };
}

function criarSessaoPortal(): Loja {
  return {
    meId: ADVOGADA.id,
    meName: ADVOGADA.name,
    portal: true,
    threads: [conversaDoEscritorio],
    unread: { [conversaDoEscritorio.conversation.id]: 1 },
    directory: [],
  };
}

const lojaInterna = criarSessaoInterna();
const lojaPortal = criarSessaoPortal();
const lojas = [lojaInterna, lojaPortal];

function loja(portal: boolean) {
  return portal ? lojaPortal : lojaInterna;
}

/** A conversa como cada lado a vê: o portal só enxerga o próprio externo. */
function vista(trilha: Trilha, estado: Loja): ChatConversation {
  const conversa = { ...trilha.conversation, readOnly: encerrada(trilha.conversation) };
  return estado.portal
    ? { ...conversa, participants: conversa.participants.filter((p) => p.userId === estado.meId) }
    : conversa;
}

function paraResumo(trilha: Trilha, estado: Loja): ChatConversationSummary {
  const { meId } = estado;
  const ultima = trilha.messages.at(-1) ?? null;
  const naoLidas = estado.unread[trilha.conversation.id] ?? 0;
  return {
    ...vista(trilha, estado),
    lastMessagePreview: ultima?.body ?? null,
    unreadCount: naoLidas,
    // TUT-491 — a mesma regra do servidor: alguém (que não eu) me mencionou.
    mentionedMe: trilha.messages.some(
      (m) => m.authorUserId !== meId && (m.mentionedUserIds ?? []).includes(meId),
    ),
    // As `unread` últimas são as não lidas: a menção entre elas acende o selo.
    unreadMentionMe:
      naoLidas > 0 &&
      trilha.messages
        .slice(-naoLidas)
        .some((m) => m.authorUserId !== meId && (m.mentionedUserIds ?? []).includes(meId)),
  };
}

/**
 * A regra de `validarMencoes` do servidor (`apps/api/src/domain/chat.ts`),
 * para o demo recusar o que a API recusa: menção só a quem participa, e nunca
 * em conversa com alguém de fora.
 */
function validarMencoesDemo(mencoes: readonly string[], autor: string, participantes: ChatConversation["participants"]): string[] {
  const unicas = [...new Set(mencoes)].filter((id) => id !== autor);
  if (unicas.length === 0) return [];
  if (participantes.some((p) => p.role === EXTERNO)) {
    throw new Error(
      "Nesta conversa há alguém de fora do escritório, que lê tudo. Para avisar alguém da equipe, use uma conversa da equipe.",
    );
  }
  const dentro = new Set(participantes.map((p) => p.userId));
  if (unicas.some((id) => !dentro.has(id))) throw new Error("Só dá para mencionar quem participa desta conversa.");
  return unicas;
}

/** Quem é de fora, pelo que a demonstração conhece (as conversas semeadas). */
function ehExterno(userId: string): boolean {
  return lojas.some((estado) =>
    estado.threads.some((t) => t.conversation.participants.some((p) => p.userId === userId && p.role === EXTERNO)),
  );
}

let contadorId = 0;

export function criarClienteDeChatDemo(portal: boolean) {
  return {
    escritorio: async () => {
      const estado = loja(portal);
      if (!estado.portal) {
        throw new ChatApiError(
          "A conversa com o escritório é de quem acessa pelo portal.",
          403,
          "CHAT_OFFICE_CONVERSATION_EXTERNAL_ONLY",
        );
      }
      // Idempotente, como o get-or-create da API: a trilha é uma só.
      return vista(conversaDoEscritorio, estado);
    },
    listConversations: async () => {
      const estado = loja(portal);
      const items = [...estado.threads]
        .sort((a, b) => (b.conversation.lastMessageAt ?? "").localeCompare(a.conversation.lastMessageAt ?? ""))
        .map((trilha) => paraResumo(trilha, estado));
      return { items };
    },
    createDirect: async (otherUserId: string) => {
      const estado = loja(portal);
      if (estado.portal) throw PORTAL_SO_ESCRITORIO();
      if (ehExterno(otherUserId)) throw EXTERNO_SO_ESCRITORIO();
      const existente = estado.threads.find(
        (t) =>
          t.conversation.kind === "DIRECT" &&
          t.conversation.participants.some((p) => p.userId === otherUserId),
      );
      if (existente) return vista(existente, estado);
      const contato = estado.directory.find((d) => d.userId === otherUserId);
      const nova: Trilha = {
        conversation: {
          id: `chat-demo-novo-${++contadorId}`,
          kind: "DIRECT",
          title: null,
          externalUserId: null,
          readOnly: false,
          participants: [
            { userId: estado.meId, name: estado.meName, role: "" },
            { userId: otherUserId, name: contato?.name ?? "Contato", role: contato?.role ?? "" },
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastMessageAt: null,
        },
        messages: [],
      };
      estado.threads.push(nova);
      return vista(nova, estado);
    },
    createGroup: async (title: string, userIds: string[]) => {
      const estado = loja(portal);
      if (estado.portal) throw PORTAL_SO_ESCRITORIO();
      if (userIds.some(ehExterno)) throw EXTERNO_SO_ESCRITORIO();
      const participantes = [
        { userId: estado.meId, name: estado.meName, role: "" },
        ...userIds.map((id) => {
          const contato = estado.directory.find((d) => d.userId === id);
          return { userId: id, name: contato?.name ?? "Contato", role: contato?.role ?? "" };
        }),
      ];
      const nova: Trilha = {
        conversation: {
          id: `chat-demo-grupo-${++contadorId}`,
          kind: "GROUP",
          title,
          externalUserId: null,
          readOnly: false,
          participants: participantes,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastMessageAt: null,
        },
        messages: [],
      };
      estado.threads.push(nova);
      return vista(nova, estado);
    },
    listMessages: async (conversationId: string) => {
      const trilha = loja(portal).threads.find((t) => t.conversation.id === conversationId);
      // Cópia, não a referência viva: a TELA guarda `items` no cache do
      // react-query e faz o próprio append otimista ao mandar mensagem. Se
      // devolvêssemos o array da loja, o `postMessage` (que dá `push` nele)
      // mutaria o MESMO array que o cache já segura, e o append otimista
      // dobraria a mensagem — foi exatamente o defeito que a e2e pegou.
      return {
        items: trilha ? [...trilha.messages] : [],
        total: trilha?.messages.length ?? 0,
      };
    },
    postMessage: async (conversationId: string, body: string, mentionUserIds: string[] = []) => {
      const estado = loja(portal);
      const trilha = estado.threads.find((t) => t.conversation.id === conversationId);
      if (!trilha) throw new Error("Conversa de demonstração não encontrada.");
      if (encerrada(trilha.conversation)) throw CONVERSA_ENCERRADA();
      const mensagem: ChatMessage = {
        // TUT-340 — prefixo próprio: `chat-demo-msg-1..3` são as mensagens
        // semeadas, e o contador recomeça em 1. Com a tela conferindo o id
        // (`acrescentarMensagem`), a primeira mensagem enviada colidia com a
        // semeada e sumia.
        id: `chat-demo-enviada-${++contadorId}`,
        conversationId,
        authorUserId: estado.meId,
        authorName: estado.meName,
        body,
        mentionedUserIds: validarMencoesDemo(mentionUserIds, estado.meId, trilha.conversation.participants),
        createdAt: new Date().toISOString(),
      };
      trilha.messages.push(mensagem);
      trilha.conversation.lastMessageAt = mensagem.createdAt;
      trilha.conversation.updatedAt = mensagem.createdAt;
      // Do outro lado da mesma trilha (a conversa com o escritório), a
      // mensagem chega como não lida.
      for (const outra of lojas) {
        if (outra !== estado && outra.threads.includes(trilha)) {
          outra.unread[conversationId] = (outra.unread[conversationId] ?? 0) + 1;
        }
      }
      return mensagem;
    },
    markRead: async (conversationId: string) => {
      const estado = loja(portal);
      if (estado.threads.some((t) => t.conversation.id === conversationId)) estado.unread[conversationId] = 0;
      return { ok: true };
    },
    directory: async () => {
      const estado = loja(portal);
      if (estado.portal) throw PORTAL_SO_ESCRITORIO();
      return { items: estado.directory };
    },
  };
}
